import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { persistStore } from 'src/persist-store';
import { preloadOrgMembers } from 'src/hooks/organization/useOrgMembers';
import { preloadOrgInvites } from 'src/hooks/organization/useOrgInvites';
import { preloadContracts } from 'src/hooks/contracts/useContractOperations';
import { preloadDeals } from 'src/hooks/deals/useDealOperations';
import { preloadFiles } from 'src/hooks/files/useFileOperations';
import { preloadNylasConnections } from 'src/hooks/inbox/useNylas';

interface OrgContextType {
  selectedOrgId: string | undefined;
  setSelectedOrgId: (orgId: string | undefined) => void;
  clearSelectedOrg: () => void;
  isInitialized: boolean;
  hasSelectedWorkspace: boolean;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

const ORG_STORAGE_KEY = 'selected-org-id';
const WORKSPACE_SELECTED_KEY = 'workspace-selected';

interface OrgProviderProps {
  children: ReactNode;
}

export const OrgProvider: React.FC<OrgProviderProps> = ({ children }) => {
  const [selectedOrgId, setSelectedOrgIdState] = useState<string | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasSelectedWorkspace, setHasSelectedWorkspace] = useState(false);

  // Load persisted org on mount.
  //
  // Stored in localStorage (not sessionStorage) so opening a new tab keeps
  // the workspace selection — sessionStorage is per-tab, which used to
  // bounce every new-tab navigation back to /organizations.
  useEffect(() => {
    const loadPersistedOrg = async () => {
      try {
        const orgId = await persistStore.getLocal<string>(ORG_STORAGE_KEY);
        const workspaceSelected = await persistStore.getLocal<boolean>(WORKSPACE_SELECTED_KEY);
        if (orgId) {
          setSelectedOrgIdState(orgId);
        }
        if (workspaceSelected) {
          setHasSelectedWorkspace(true);
        }
      } catch (error) {
        console.error('OrgContext: Error loading persisted org:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    loadPersistedOrg();

    // Cross-tab sync: when another tab switches workspace, follow it.
    // Browsers fire `storage` events on OTHER tabs only, not the writer.
    const onStorage = (e: StorageEvent) => {
      if (e.key === ORG_STORAGE_KEY) {
        const next = e.newValue ? JSON.parse(e.newValue) : undefined;
        setSelectedOrgIdState(typeof next === 'string' ? next : undefined);
      } else if (e.key === WORKSPACE_SELECTED_KEY) {
        const next = e.newValue ? JSON.parse(e.newValue) : false;
        setHasSelectedWorkspace(Boolean(next));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setSelectedOrgId = async (orgId: string | undefined) => {
    setSelectedOrgIdState(orgId);
    setHasSelectedWorkspace(true);

    // Pre-fetch org-specific data for instant navigation
    if (orgId) {
      preloadOrgMembers(orgId);
      preloadOrgInvites(orgId);
      preloadContracts(orgId);
      preloadDeals(orgId);
      preloadFiles(orgId);
      // CONNECTIONS ONLY: warm the Nylas connections cache (the cold-start hop
      // that gates the inbox — connectionId derives from connections[0]). Not
      // org-scoped (the API resolves connections from the auth session), so no
      // orgId arg. Threads are NOT chained here — that would require reading the
      // SWR cache imperatively to find connections[0]; Item 2's stream provider
      // owns the threads warm-up from the connections it already holds.
      preloadNylasConnections();
    }

    try {
      if (orgId) {
        await persistStore.putLocal<string>(ORG_STORAGE_KEY, orgId);
      } else {
        await persistStore.removeLocal(ORG_STORAGE_KEY);
      }
      await persistStore.putLocal<boolean>(WORKSPACE_SELECTED_KEY, true);
    } catch (error) {
      console.error('OrgContext: Error saving org selection:', error);
    }
  };

  const clearSelectedOrg = async () => {
    setSelectedOrgIdState(undefined);

    try {
      await persistStore.removeLocal(ORG_STORAGE_KEY);
    } catch (error) {
      console.error('OrgContext: Error clearing org selection:', error);
    }
  };

  const value: OrgContextType = {
    selectedOrgId,
    setSelectedOrgId,
    clearSelectedOrg,
    isInitialized,
    hasSelectedWorkspace,
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = (): OrgContextType => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};